import { type AgentDataPut, AgentDataView, SamplesLogResponse } from '@tabel/shared/schemas/agent-data';
import { ApiError } from '../../lib/errors';
import { contextHeaders } from '../../lib/role';

/*
 * Адреса страницы «Данные для ИИ-агентов» (005, T139; specs/005-rules-mcp/contracts/http-api.md; data-model.md, Б).
 * Формы ответов и тела — общие схемы `@tabel/shared/schemas/agent-data` (их же отдаёт модуль `agent` сервера, T132,
 * T136); ответ не по схеме — сбой сервера с кодом ошибки, а не пустая форма. Лишние поля ответа дальше не идут.
 * - `GET /api/admin/agent-data` — разрешение на выборки объекта (`AgentDataView`): включено, основание, клиенты,
 *   лимиты, клиенты на выбор (`available`) и история, новые сверху;
 * - `PUT /api/admin/agent-data` — включить, выключить, изменить (`AgentDataPut`); ответ — то же разрешение после
 *   записи. Без основания включение отклоняется (`400 BASIS_REQUIRED`, FR-028), без клиентов или с чужим клиентом —
 *   `400 VALIDATION`; выключение отзывает ссылки выборок;
 * - `GET /api/admin/agent-data/samples?from&to&personId?` — журнал выборок (`SamplesLogResponse`): у каждой выборки
 *   человеко-дни; с `personId` — только выборки с днями этого человека и только его дни (FR-026).
 * Все — под входом сессии 004 с заголовками роли и объекта, только `sysadmin`. Ошибки — тело 004
 * `{ error: { code, message, requestId, details? } }`.
 */

export type {
  AgentDataClient,
  AgentDataPut,
  AgentDataRevision,
  AgentDataView,
  SampleLogEntry,
  SamplesLogResponse,
} from '@tabel/shared/schemas/agent-data';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isText = (v: unknown): v is string => typeof v === 'string';

/** Ответ не по схеме (например, HTML вместо JSON) — сбой сервера с кодом ошибки. */
const badAnswer = (requestId: string | null) => new ApiError(500, 'INTERNAL', 'Ошибка сервера', requestId);

async function call(
  method: 'GET' | 'PUT',
  url: string,
  body?: unknown,
): Promise<{ data: unknown; id: string | null }> {
  let res: Response;
  try {
    // `Origin` браузер ставит сам, сервер его проверяет у изменяющих запросов (004).
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        ...contextHeaders(),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером', null);
  }
  const requestId = res.headers.get('x-request-id');
  const data: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const e = isObject(data) && isObject(data.error) ? data.error : {};
    const code = isText(e.code)
      ? (e.code as ApiError['code'])
      : res.status === 404
        ? 'NOT_FOUND'
        : 'INTERNAL';
    // Сессия кончилась — страница входа, как у остальных страниц (`lib/api.ts`).
    if (res.status === 401 && code === 'UNAUTHENTICATED') location.assign('/login');
    const details = isObject(e.details) ? e.details : undefined;
    throw new ApiError(
      res.status,
      code,
      isText(e.message) ? e.message : `Ошибка ${res.status}`,
      isText(e.requestId) ? e.requestId : requestId,
      typeof details?.retryAfterSec === 'number' ? details.retryAfterSec : undefined,
      details,
    );
  }
  return { data, id: requestId };
}

const BASE = '/api/admin/agent-data';

/** Разбор ответа по схеме: не по схеме — ошибка, иначе форма показала бы пустые значения. */
function parsed<T>(schema: { safeParse(v: unknown): { success: true; data: T } | { success: false } }) {
  return ({ data, id }: { data: unknown; id: string | null }): T => {
    const r = schema.safeParse(data);
    if (!r.success) throw badAnswer(id);
    return r.data;
  };
}

/** Разрешение на выборки объекта. */
export async function agentData(): Promise<AgentDataView> {
  return parsed(AgentDataView)(await call('GET', BASE));
}

/** Записать разрешение; ответ — разрешение после записи (с новой записью истории). */
export async function saveAgentData(body: AgentDataPut): Promise<AgentDataView> {
  return parsed(AgentDataView)(await call('PUT', BASE, body));
}

/** Журнал выборок за период (даты включительно); с `personId` — выборки и дни этого человека. */
export async function samplesLog(q: {
  from: string;
  to: string;
  personId?: string;
}): Promise<SamplesLogResponse> {
  const p = new URLSearchParams({ from: q.from, to: q.to });
  if (q.personId) p.set('personId', q.personId);
  return parsed(SamplesLogResponse)(await call('GET', `${BASE}/samples?${p}`));
}
